/**
 * The queue's actions, the same for the screens whether the queue runs on the queue service (dabbeh)
 * (JABAZ_API_URL set) or on this device only (prototype). The service is the source of truth when on:
 * every action calls it first and the store only mirrors its answer.
 */
import { book } from '@/common/apis/services/booking/book';
import { ClinicStatus } from '@/common/constants/status/clinicStatus';
import { useUserInfoStore, type UserInfo } from '@/modules/login/store/userInfo';
import { useEffect, useState } from 'react';
import { QueueApiError, isQueueApi, queueApi, toMembership, type ServerSeat } from './api';
import { isJabazRolloutSlug } from './rollout';
import { useJabazStore } from './store/jabazStore';
import type { JabazDoctor, JabazMembership, JabazSubscription } from './types';

/** Bumped by every change this device makes, so a slower sync that started before it does not undo it. */
let changes = 0;

/** A seat joined on another device: the doctor's name from the profile address until the profile is opened here. */
export const doctorFromSlug = (slug: string): JabazDoctor => {
  let name = slug;
  try {
    name = decodeURIComponent(slug);
  } catch {
    // keep as is
  }
  return { slug, name: name.replace(/^دکتر-/, '').replace(/-\d+$/, '').replace(/-/g, ' ') };
};

const mirror = (seat: ServerSeat, viaSubscription?: boolean) => {
  const state = useJabazStore.getState();
  const known = state.memberships[seat.slug];
  const doctor = known?.doctor ?? (state.draft?.slug === seat.slug ? state.draft : doctorFromSlug(seat.slug));
  const membership = toMembership(seat, doctor, viaSubscription ?? known?.viaSubscription ?? false);
  changes += 1;
  useJabazStore.setState({ memberships: { ...useJabazStore.getState().memberships, [seat.slug]: membership } });
  return membership;
};

/** This doctor's seat as the service has it now (mirrored into the store), or null when there is none. */
const latestSeat = async (slug: string) => {
  const seats = await queueApi.mySeats();
  const found = seats.find(item => item.slug === slug);
  return found ? mirror(found) : null;
};

/** The patient's seats from the service (rollout doctors only); a no-op in the prototype. */
export const syncQueueSeats = async () => {
  if (!isQueueApi()) return;
  const startedAt = changes;
  const seats = await queueApi.mySeats();
  if (startedAt !== changes) return;
  const state = useJabazStore.getState();
  state.syncSeats(
    seats
      .filter(seat => isJabazRolloutSlug(seat.slug))
      .map(seat => {
        const known = state.memberships[seat.slug];
        const doctor = known?.doctor ?? (state.draft?.slug === seat.slug ? state.draft : doctorFromSlug(seat.slug));
        return toMembership(seat, doctor, known?.viaSubscription ?? false);
      }),
  );
};

/**
 * Keeps the seats in step with the service while a queue screen is open: at once, every 20 s while the
 * tab is visible, and when the patient comes back to the tab. `synced` turns true after the first answer
 * (or at once in the prototype / logged out), so a page can wait for it before deciding a seat is gone.
 */
export const useQueueSync = (active = true) => {
  const isLogin = useUserInfoStore(state => state.isLogin);
  const pending = useUserInfoStore(state => state.pending);
  const [synced, setSynced] = useState(!isQueueApi());
  useEffect(() => {
    if (!isQueueApi() || !active) return;
    if (!isLogin) {
      if (!pending) setSynced(true);
      return;
    }
    let cancelled = false;
    const run = () =>
      syncQueueSeats()
        .catch(() => undefined)
        .finally(() => !cancelled && setSynced(true));
    run();
    const id = window.setInterval(() => document.visibilityState === 'visible' && run(), 20_000);
    window.addEventListener('focus', run);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', run);
    };
  }, [active, isLogin, pending]);
  return synced;
};

/** People waiting in a doctor's queue, from the service (null in the prototype or when it cannot be read). */
export const fetchQueueSummary = async (slug: string) => {
  if (!isQueueApi()) return null;
  try {
    return await queueApi.summary(slug);
  } catch {
    return null;
  }
};

/** After paying (or with an active subscription): a seat in the draft doctor's queue. */
export const joinQueue = async ({ paid, viaSubscription }: { paid?: JabazSubscription | null; viaSubscription: boolean }) => {
  const store = useJabazStore.getState();
  const doctor = store.draft;
  if (!doctor) return null;
  if (!isQueueApi()) return viaSubscription ? store.joinWithSubscription(doctor) : store.purchase(paid);
  const seat = await queueApi.join(doctor.slug);
  if (!viaSubscription) store.purchase(paid);
  return mirror(seat, viaSubscription);
};

export const leaveQueue = async (slug: string) => {
  if (isQueueApi()) await queueApi.leave(slug);
  changes += 1;
  useJabazStore.getState().leave(slug);
};

/** «انصراف از این نوبت»: the appointment goes to the next person, the patient to the end of the line. */
export const passQueueOffer = async (seat: JabazMembership) => {
  if (!isQueueApi() || !seat.offer?.id) return useJabazStore.getState().passOffer(seat.doctor.slug);
  return mirror(await queueApi.pass(seat.offer.id));
};

/** The hold ran out on screen: show it at once, then take the service's word for the new place. */
export const holdEnded = (seat: JabazMembership) => {
  changes += 1;
  useJabazStore.getState().passOffer(seat.doctor.slug);
  if (isQueueApi()) window.setTimeout(() => void syncQueueSeats().catch(() => undefined), 3_000);
};

/** The logged-in user once the site has finished loading it (a tap right after the page opens must not miss it). */
export const loadedUser = (timeoutMs = 8_000) =>
  new Promise<{ isLogin: boolean; info: UserInfo }>(resolve => {
    const done = () => {
      const { isLogin, info } = useUserInfoStore.getState();
      resolve({ isLogin, info });
    };
    if (!useUserInfoStore.getState().pending) return done();
    const timer = window.setTimeout(() => {
      unsubscribe();
      done();
    }, timeoutMs);
    const unsubscribe = useUserInfoStore.subscribe(state => {
      if (state.pending) return;
      window.clearTimeout(timer);
      unsubscribe();
      done();
    });
  });

/**
 * The patient's own details are complete enough for the clinic to book without the booking form. Anything
 * missing (or a foreign national) goes through the regular booking form, on the same held slot.
 */
export const canBookDirectly = (user: UserInfo) =>
  !!user.id && !!user.name && !!user.family && !!user.cell && !!user.national_code && !user.is_foreigner;

export type ConfirmResult =
  /** booked; open this address (receipt, factor or the payment gateway) */
  | { kind: 'done'; url: string; external?: boolean }
  /** the regular booking form for the held slot (prototype, missing details, or a clinic that asks more) */
  | { kind: 'form'; url: string; message?: string }
  /** the clinic's hold lapsed and the nearest free slot is a different time: shown on the card to confirm again */
  | { kind: 'changed'; message: string }
  /** the slot is gone and no other is free right now; the patient stays first in line */
  | { kind: 'gone'; message: string };

/**
 * «تأیید و دریافت نوبت»: books the held slot in the patient's own name with the site's regular booking
 * call (the same `/api/book` as the booking form), then tells the service. The clinic holds a slot for
 * 5 minutes; when that ran out, the service holds a fresh one for the same patient: the same time is booked
 * at once, a different time goes back to the card for the patient to confirm (never booked unseen).
 */
export const confirmQueueOffer = async (
  seat: JabazMembership,
  user: UserInfo,
  { formUrl, isWebView }: { formUrl: (seat: JabazMembership) => string; isWebView: boolean },
): Promise<ConfirmResult> => {
  const toForm = (current: JabazMembership, message?: string): ConfirmResult => ({ kind: 'form', url: formUrl(current), message });
  if (!isQueueApi() || !seat.offer?.id) {
    useJabazStore.getState().confirmOffer(seat.doctor.slug);
    return toForm(seat);
  }
  // the service takes the held slot again every few minutes (the clinic's hold lasts 5): book with the
  // request code it holds right now, not the one this page loaded
  let current = (await latestSeat(seat.doctor.slug).catch(() => null)) ?? seat;
  if (!current.offer?.id) return { kind: 'gone', message: 'این نوبت دیگر در دسترس نیست. با آزاد شدن نوبت بعدی، دوباره خبرتان می‌کنیم.' };
  if (current.offer.visitAt !== seat.offer.visitAt)
    return { kind: 'changed', message: 'نوبت قبلی دیگر خالی نبود؛ نزدیک‌ترین نوبت خالی برای شما رزرو شد. زمان نوبت را ببینید و دوباره تأیید کنید.' };
  if (!canBookDirectly(user)) return toForm(current);

  let renewedRetry = false;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const offer = current.offer!;
    if (!offer.requestCode || !offer.centerId || !offer.serverId || !offer.userCenterId) return toForm(current);
    const { data } = await book({
      request_code: offer.requestCode,
      center_id: offer.centerId,
      server_id: offer.serverId,
      user_center_id: offer.userCenterId,
      is_webview: isWebView ? 1 : 0,
      first_name: user.name!,
      last_name: user.family!,
      cell: `0${user.cell}`,
      national_code: user.national_code!,
      selected_user_id: user.id!,
      is_foreigner: false,
      ...(user.gender && { gender: user.gender }),
      ...(user.father_name && { father_name: user.father_name }),
      ...(user.birth_date && { birth_date: user.birth_date }),
    } as Parameters<typeof book>[0]);

    if (data?.status === ClinicStatus.SUCCESS) {
      const bookId = String(data.book_info?.id ?? '');
      // the appointment is the patient's either way; the seat ends with it
      await queueApi.booked(offer.id!, bookId).catch(() => undefined);
      changes += 1;
      useJabazStore.getState().leave(seat.doctor.slug);
      if (data.payment?.reqiure_payment === '1') {
        if (String(offer.serverId) === '1') return { kind: 'done', url: `/factor/${offer.centerId}/${bookId}` };
        return { kind: 'done', url: String(data.payment.redirect_url), external: true };
      }
      return { kind: 'done', url: `/receipt/${offer.centerId}/${bookId}` };
    }

    if (data?.status === ClinicStatus.EXPIRE_TIME_SLOT && !renewedRetry) {
      // booked in the second the service was taking the same slot again: same time, new code
      renewedRetry = true;
      const latest = await latestSeat(seat.doctor.slug).catch(() => null);
      const renewed = latest?.offer;
      if (latest && renewed && renewed.id === offer.id && renewed.visitAt === offer.visitAt && renewed.requestCode !== offer.requestCode) {
        current = latest;
        attempt -= 1;
        continue;
      }
    }

    if (data?.status === ClinicStatus.EXPIRE_TIME_SLOT && attempt === 0) {
      try {
        current = mirror(await queueApi.refresh(offer.id!));
        if (!current.offer?.id) break;
        if (current.offer.visitAt !== offer.visitAt)
          return { kind: 'changed', message: 'نوبت قبلی دیگر خالی نبود؛ نزدیک‌ترین نوبت خالی برای شما رزرو شد. زمان نوبت را ببینید و دوباره تأیید کنید.' };
        continue;
      } catch (error) {
        if (error instanceof QueueApiError && error.status === 409) {
          void syncQueueSeats().catch(() => undefined);
          return { kind: 'gone', message: error.message };
        }
        throw error;
      }
    }
    // the clinic wants something the form asks for (insurance, details…): same slot, regular form
    return toForm(current, data?.message);
  }
  void syncQueueSeats().catch(() => undefined);
  return { kind: 'gone', message: 'این نوبت دیگر در دسترس نیست. با آزاد شدن نوبت بعدی، دوباره خبرتان می‌کنیم.' };
};
